import React, { useEffect, useMemo, useState } from 'react';

import styles from '../css/citations.module.css';
import { Author, ReferenceEntry } from '../models/aggregatorData';
import { FacetsUpdated, LinkClick, useGlobalState } from './AppState';
import FilterGroup from './FilterGroup';
import ManuscriptInfo from './ManuscriptInfo';


const UTF2000 = " "

const dedupe = (a:any[],b:string)=>{
    if(a.indexOf(b)<0) {
      a.push(b)
    }
    return a;
}

const DateToString = (date:{year:string, month:string, day:string} | undefined) =>{
  function toMonthName(monthNumber:number) {
    const date = new Date();
    date.setMonth(monthNumber - 1);

    // 👇️ using visitor's default locale
    return date.toLocaleString([], {
      month: 'long',
    });
  }

  if(date === undefined || date === null){
    return null
  }
  if((date.year||0) === 0){
    return null
  }
  //If only Year is available
  if((date.month||0) === 0){
    return date.year
  }

  //If Month is available in addition to Year
  if((date.day||0) === 0){
    return toMonthName(parseInt(date.month)) + " " + date.year
  }

  return parseInt(date.day) + " " + toMonthName(parseInt(date.month)) + " " + date.year
}

const createReferenceListDetails = (row:ReferenceEntry, index:number) =>{

  let hasDocType = !!row.documentType
  let hasOpenAccessBool = !!row.isOpenAccess

  if(hasDocType || hasOpenAccessBool){
    return <div className={styles.mReferencesListDetailsDoc} key={`ref_row_details_type_${index + 1}`}>
      <div className={`${styles.mReferencesListDetailsDocType}`}>
        {row.documentType}
      </div>
      {row.isOpenAccess && row.pdfUrl &&<>
        <div>•</div>
        <a href={row.pdfUrl}
          className={`${styles.mReferencesListDetailsOpenAccess}`}>Open
          Access</a>
      </>}
    </div>
  } else {
    return <div className={styles.mReferencesListDetailsDoc}>
      <div className={`${styles.mReferencesListDetailsDocType}`}>
      </div>
    </div>
  }
}

const createReferenceListDetailsInfo = (row:ReferenceEntry, index:number) => {

  let listDetailsInfoList = []

  let hasPublication = !!row.publication
  let hasDate = !!row.date && DateToString(row.date)

  if(hasPublication){
    listDetailsInfoList.push(<div key={`ref_row_details_publication_${index + 1}`}>{row.publication}</div>)
  }

  if(hasDate){
    listDetailsInfoList.push(<span key={`ref_row_details_date_${index + 1}`}>{DateToString(row.date)}</span>)
  }
  return <div className={`${styles.mReferencesListDetailsInfo}`}>
    {listDetailsInfoList}
  </div>
}

const createReferenceListAuthors = (row:ReferenceEntry, index:number) => {

  return <div className={`${styles.mReferencesListDetailsAuthors}`} key={`ref_row_details_authors_${index + 1}`}>
    <div>
      {

        //The quoted string below does NOT contain a space
        //it is a 0.5em wide whitespace character (U+2000)
        row.authors?.map(author => author.fullName).join("," + UTF2000)
      }
    </div>
  </div>

}

const ReferencesTable = ({ references, title, authors }: { references: ReferenceEntry[], title:string, authors:Author[] }) => {


  interface AllFilterOptionsIface {
    yearPublished: {
      options:string[]
    },
    documentType: {
      options:string[]
    },
    publication: {
      options:string[]
    },
    authors: {
      options:string[]
    }
    relationships: {
      options: string[]
    }
  }

  const allFilterOptions: AllFilterOptionsIface = useMemo(()=>{
    return {
      yearPublished: {options:
        references
          .map(v=> {
            return v?.date?.year??""
          })
          .sort()
          .reverse()
      },
      documentType: {options:
          references
            .map(v=> {
              return v?.documentType??""
            })
            .sort()
      },
      publication: {options:
          references
            .map(v=> {
              return v.publication??""
            })
            .sort()
      },
      authors: {options:
          references
            //Convert to array of arrays of authors
            .map(v=> {return v.authors})
            //Convert into a flat list
            .flat()
            //Remove all empty values in the flattened list
            .map(v=>{return v?.fullName??""})
            //Sort the authors
            .sort()
      },
      relationships: {
        options:
          references
            //Convert to array of array of attributes
            .map(v => { return v.citationAttributes })
            //Convert into a flat list
            .flat()
            //Remove all empty values in the flattened list
            .map(v => { return v ?? "" })
            //Sort the attributes
            .sort()
      },
    }
  }, [references])

  const YEAR_PUBLISHED = 'date?.year??`Unknown`';
  const DOCUMENT_TYPE = 'documentType??`Unknown`';
  const PUBLICATION = 'publication??`Unknown`';
  const AUTHORS = 'authors??`No authors found`';
  const RELATIONSHIPS = 'citationAttributes??`No relationships found`';

  interface IMap<T> {
    [index: string]: T;
    [index: number]: T;
  }

  let [selectedGroups, setSelectedGroups] = useState<IMap<string[]>>({
    [YEAR_PUBLISHED]:[],
    [DOCUMENT_TYPE]:[],
    [PUBLICATION]:[],
    [AUTHORS]: [],
    [RELATIONSHIPS]: []
  })

  const { analyticsDispatcher, globalState } = useGlobalState();

  function handleLinkClick(e: any) {
    analyticsDispatcher(LinkClick(e.target.dataset, globalState));
  }

  function handleFacetsUpdated(){
    analyticsDispatcher(FacetsUpdated(
      [{
        id: 'documentType',
        values: selectedGroups[DOCUMENT_TYPE]
      },
      {
        id: 'author',
        values: selectedGroups[AUTHORS]
      },
      {
        id: 'publication',
        values: selectedGroups[PUBLICATION]
      },
      {
        id: 'yearPublished',
        values: selectedGroups[YEAR_PUBLISHED]
        },
      {
        id: 'relationship',
        values: selectedGroups[RELATIONSHIPS]
      }], globalState
    ))
  }

  useEffect(()=>{
    handleFacetsUpdated()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups])

  interface OnChangeIface {
    target :{
      name:string
    }
  }

  const onCheckboxChange = (key:string)=>{

    return function (e:OnChangeIface){
      let temp:string[] = selectedGroups[key]
      let valueIndex:number = temp.findIndex((v)=> v===e.target.name)??"-1"
      if(valueIndex>=0){
        delete temp[valueIndex]
        //Remove any empty values in the array after deletion
        temp = temp.filter(function (el) {
          return el != null;
        });
      } else {
        temp.push(e.target.name)
      }

      setSelectedGroups(
        {
          key:temp,
          ...selectedGroups
        }
      )
    }
  };

  const clearAllFilters = () =>{
    setSelectedGroups({
      [YEAR_PUBLISHED]:[],
      [DOCUMENT_TYPE]:[],
      [PUBLICATION]:[],
      [AUTHORS]:[],
      [RELATIONSHIPS]:[]
    })
  }

  let rows = references

  for (const filterGroupSelections in selectedGroups) {
    if (filterGroupSelections==="key"){
      continue
    }
    for (const selectionIndex in selectedGroups[filterGroupSelections]) {

      rows = rows.filter(v=>{
        let rowValue = eval('v.' + filterGroupSelections) //The value associated with the given reference
        if(Array.isArray(rowValue) && rowValue.every((item: any) => typeof item === "string")) {
          return selectedGroups[filterGroupSelections].some(r=> rowValue.includes(r))
        }
        else if(Array.isArray(rowValue) && rowValue.every((item: any) => typeof item === "object")) {
          return selectedGroups[filterGroupSelections].some(r=> rowValue.find((obj: { fullName: string; }) => obj.fullName === r))
        } else {
          return selectedGroups[filterGroupSelections].includes(rowValue)
        }
      })
    }
  }

  return (
    <React.Fragment>
      <div className={styles.mLeftNav} role={"list"}>
        <span className={styles.mReferencesFilter}>Filter</span>
        <span className={styles.mReferencesFilterClear} onClick={()=>{clearAllFilters()}}>Clear All</span>
        <FilterGroup key={"year"} header={"year published"} filterOptions={allFilterOptions.yearPublished.options.reduce(dedupe,[])}
                     rawValuesForCount={allFilterOptions.yearPublished.options}
                     nullOptionReplacement={"Unknown"} sortByOccurance={false}
                     selected={selectedGroups[YEAR_PUBLISHED]} onChangeCallback={onCheckboxChange(YEAR_PUBLISHED)}/>
        <FilterGroup key={"doc"} header={"document type"} filterOptions={allFilterOptions.documentType.options.reduce(dedupe,[])}
                     rawValuesForCount={allFilterOptions.documentType.options}
                     nullOptionReplacement={"Unknown"} sortByOccurance={true}
                     selected={selectedGroups[DOCUMENT_TYPE]} onChangeCallback={onCheckboxChange(DOCUMENT_TYPE)}/>
        <FilterGroup key={"pub"} header={"publication"} filterOptions={allFilterOptions.publication.options.reduce(dedupe,[])}
                     rawValuesForCount={allFilterOptions.publication.options}
                     nullOptionReplacement={"Unknown"} sortByOccurance={true}
                     selected={selectedGroups[PUBLICATION]} onChangeCallback={onCheckboxChange(PUBLICATION)}/>
        <FilterGroup key={"author"} header={"authors"} filterOptions={allFilterOptions.authors.options.reduce(dedupe,[])}
                     rawValuesForCount={allFilterOptions.authors.options}
                     nullOptionReplacement={"No authors found"} sortByOccurance={true}
                     selected={selectedGroups[AUTHORS]} onChangeCallback={onCheckboxChange(AUTHORS)} />
        <FilterGroup key={"citationAttributes"} header={"relationship"} filterOptions={allFilterOptions.relationships.options.reduce(dedupe, [])}
                     rawValuesForCount={allFilterOptions.relationships.options}
                     nullOptionReplacement={"Independent"} sortByOccurance={true}
                     selected={selectedGroups[RELATIONSHIPS]} onChangeCallback={onCheckboxChange(RELATIONSHIPS)} />
      </div>
      <ManuscriptInfo
        authors={authors}
        manuscriptTitle={title}
      />
      <div role="none" className={`${styles.mRefListContainer}`}>
        <div className={styles.totalReferencesFilteredCount}>
          Showing {rows.length} of {references.length} References
        </div>
        <div role="list">
          {rows.map((row, index) => {
            return (
              <div className={styles.mReferencesListBox} role="listitem" key={`ref_row_${row.referenceId}_${index}`}>
                <div className={styles.mReferencesListInd} key={`ref_row_index_${row.referenceId}`}>
                  {index + 1}
                </div>
                <div className={styles.mReferencesListDetails} key={`ref_row_details_${index + 1}`}>
                  {createReferenceListDetails(row, index)}
                  <div className={styles.mReferencesListDetailsDocName} key={`ref_row_details_docName_${index + 1}`}>
                    {row.title}
                  </div>
                  {createReferenceListAuthors(row, index)}
                  {createReferenceListDetailsInfo(row, index)}
                  <div className={`${styles.mReferencesListDetailsRefLinks}`} key={`ref_row_details_url_${index + 1}`}>
                    {row.pdfUrl &&
                      <a href={row.pdfUrl}
                        target={"_blank"}
                        rel = "noopener noreferrer"
                        onClick={handleLinkClick}
                        data-item-id={index + 1}
                        data-ref-type={row.documentType}
                        data-ref-title={row.title}
                        data-item-location={"View at Publisher"}>
                        {"View at Publisher"}
                      </a>
                    }
                    {row.pubmedUrl &&
                    <a href={row.pubmedUrl}
                       target={"_blank"}
                       rel = "noopener noreferrer"
                       onClick={handleLinkClick}
                       data-item-id={index + 1}
                       data-ref-type={row.documentType}
                       data-ref-title={row.title}
                       data-item-location={"View on PubMed"}>
                      {"View on PubMed"}
                    </a>
                    }
                    {row.scopusUrl &&
                    <a href={row.scopusUrl}
                       target={"_blank"}
                       rel = "noopener noreferrer"
                       onClick={handleLinkClick}
                       data-item-id={index + 1}
                       data-ref-type={row.documentType}
                       data-ref-title={row.title}
                       data-item-location={"View on Scopus"}>
                      {"View on Scopus"}
                    </a>
                    }
                  </div>
                </div>
                {row.citedbyCount === undefined ?
                  <></>
                  :
                  <div className={styles.mReferencesListCnt} key={`ref_row_details_count_${index + 1}`}>
                    <div style={{
                      fontSize: "24px",
                      lineHeight: "32px"
                    }}>{row.citedbyCount}</div>
                    <div style={{ fontSize: "14px", lineHeight: "24px" }}>Citations</div>
                  </div>
                }
              </div>
            );
          })}
        </div>
      </div>
    </React.Fragment>
  );
}

export default ReferencesTable;
